// @flow
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useSelectedProperty from './useSelectedProperty';
// eslint-disable-next-line max-len
import useAffordableWaitlistValidations from '../containers/ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';
import { useFetchWaitlistConfiguration } from './data-fetching/useFetchWaitlistV2';
import { getApplicationWindow } from '../utils/affordable';
import type { ApplicationWindow } from '../utils/affordable';

/**
 * Whether the property's waitlist can take a household at all, for the buttons that open the
 * wizard. On V2 a closed waitlist with both dates still takes households dated inside them, so
 * only a policy with no date range blocks. Until V2 answers with a policy — flag off, still
 * loading, or no configuration — the legacy closed flag stays the answer.
 *
 * `isV2WaitlistEnabled` is the V2 configuration's own on/off switch. It decides whether the V2
 * wizards apply, independent of the V1 HUD/PH rule. The flag check is required because the
 * query can return cached data after the flag turns off.
 */
const useWaitlistApplicationWindow = (): {
  ...ApplicationWindow,
  isEntryBlocked: boolean,
  isV2WaitlistEnabled: boolean,
} => {
  const { affordableWaitlistV2 = false } = useFlags();
  const selectedProperty = useSelectedProperty();
  const { isAffordableWaitlistPropertySettingStatusClosed } =
    useAffordableWaitlistValidations();
  const { data: waitlistConfiguration } = useFetchWaitlistConfiguration(
    selectedProperty?.id ?? '',
    affordableWaitlistV2,
  );

  const applicationWindow = useMemo(
    () => getApplicationWindow(waitlistConfiguration),
    [waitlistConfiguration],
  );

  const isEntryBlocked =
    affordableWaitlistV2 && applicationWindow.hasPolicy
      ? !applicationWindow.acceptsAnyDate
      : Boolean(isAffordableWaitlistPropertySettingStatusClosed);

  const isV2WaitlistEnabled =
    affordableWaitlistV2 && waitlistConfiguration?.enabled === true;

  return { ...applicationWindow, isEntryBlocked, isV2WaitlistEnabled };
};

export default useWaitlistApplicationWindow;
